import type Phaser from 'phaser';

import { TutorialPointer } from './tutorial-pointer.js';

/** On the ground and its cover (flowers at -500), under the move marker and every sprite. */
const RING_DEPTH = -450;
const RING_FILL = 0xffd23b;
const RING_OUTLINE = 0x2a1a06;
/** A dash of the ring every this many pixels of its circumference. */
const DASH_EVERY_PX = 10;

/**
 * Where a quest's bait may be laid (ADR 0047): a dashed golden ring on the
 * ground around the nearest den, as wide as the reach of the bait, and the
 * tutorial's arrow over its centre, waiting at the screen's edge when the
 * den is out of sight. Drawn by whole pixels (CLAUDE.md rule 6); a hint of
 * this client alone, which decides nothing.
 */
export class DenGuide {
  private readonly ring: Phaser.GameObjects.Graphics;
  private readonly arrow: TutorialPointer;
  private radius = 0;

  constructor(scene: Phaser.Scene) {
    this.ring = scene.add.graphics().setDepth(RING_DEPTH).setVisible(false);
    this.arrow = new TutorialPointer(scene);
  }

  /** Mark the den at `centre` (world pixels) within `radius` pixels; undefined hides it all. */
  show(centre: { x: number; y: number } | undefined, radius: number, time: number): void {
    if (centre === undefined) {
      this.ring.setVisible(false);
      this.arrow.point(undefined, time);
      return;
    }
    if (radius !== this.radius) this.draw(radius);
    this.ring.setPosition(Math.round(centre.x), Math.round(centre.y)).setVisible(true);
    this.arrow.point({ x: Math.round(centre.x), y: Math.round(centre.y) }, time);
  }

  destroy(): void {
    this.ring.destroy();
    this.arrow.destroy();
  }

  private draw(radius: number): void {
    this.radius = radius;
    this.ring.clear();
    const dashes = Math.max(8, Math.round((2 * Math.PI * radius) / DASH_EVERY_PX));
    const spots = Array.from({ length: dashes }, (_, k) => {
      const angle = (k / dashes) * Math.PI * 2;
      return { x: Math.round(Math.cos(angle) * radius), y: Math.round(Math.sin(angle) * radius) };
    });
    this.ring.fillStyle(RING_OUTLINE, 0.9);
    for (const { x, y } of spots) this.ring.fillRect(x - 2, y - 2, 4, 4);
    this.ring.fillStyle(RING_FILL, 1);
    for (const { x, y } of spots) this.ring.fillRect(x - 1, y - 1, 2, 2);
  }
}
